import React, { useState, useEffect } from "react";
import {
  Calendar,
  momentLocalizer,
  Event,
  EventProps,
} from "react-big-calendar";
import moment from "moment";
import "moment/locale/zh-cn";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  useDataProvider,
  ReferenceInput,
  AutocompleteInput,
  SimpleForm,
} from "react-admin";
import { IDriverSchedule } from "../../models/DriverSchedule";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, TextField } from "@mui/material";
import { createTrucksFilterQuery } from "./common";

const localizer = momentLocalizer(moment);
moment.locale("zh-cn");

interface ScheduleEvent extends Event {
  id?: string;
  title: string;
  start: Date;
  end: Date;
  fleetAssignmentId: string;
  desc?: string;
}

const EventComponent: React.FC<EventProps<ScheduleEvent>> = ({ event }) => (
  <span>
    <strong>{event.title}</strong> | <span>{event.desc}</span>
  </span>
);

const ScheduleCalendar: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const driverId = queryParams.get("driverId");
  console.log(`ScheduleCalendar driverId: ${driverId}`);
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(
    driverId || null
  );
  const [originCity, setOriginCity] = useState<string | null>(null);
  const [destinationCity, setDestinationCity] = useState<string | null>(null);
  const dataProvider = useDataProvider();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDriverSchedules = async () => {
      try {
        const filter: any = {};
        if (selectedDriver) filter.driver = selectedDriver;
        if (originCity) filter["origin.city"] = originCity;
        if (destinationCity) filter["destination.city"] = destinationCity;

        const { data: schedules } = await dataProvider.getList(
          "driverSchedules",
          {
            pagination: { page: 1, perPage: 10 },
            sort: { field: "id", order: "ASC" },
            filter,
          }
        );

        const driverIds = schedules.map(
          (schedule: IDriverSchedule) => schedule.driver
        );
        if (driverIds.length > 0) {
          const { data: drivers } = await dataProvider.getMany(
            "fleetAssignments",
            { ids: driverIds }
          );

          const formattedEvents = schedules.map((schedule: IDriverSchedule) => {
            const driver = drivers.find(
              (driver: any) => driver.id === schedule.driver
            );
            const driverName = driver?.driver?.name ?? "未知司机";
            const licensePlate =
              driver?.truck?.licensePlate ?? driver?.truck?.plate ?? "未知车牌";
            const source = driver?.source ?? "线下收集";

            return {
              id: schedule.id,
              title: `${driverName} - ${licensePlate} - ${source}`,
              start: new Date(schedule.departureDate),
              end: new Date(schedule.arrivalDate),
              fleetAssignmentId: schedule.driver,
              desc: `从 ${schedule.origin.province}${schedule.origin.city}${schedule.origin.district} 到 ${schedule.destination.province}${schedule.destination.city}${schedule.destination.district}`,
            };
          });

          setEvents(formattedEvents);
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.error("Error fetching driver schedules:", error);
      }
    };

    fetchDriverSchedules();
  }, [dataProvider, selectedDriver, originCity, destinationCity]);

  const handleSelectSlot = ({ start }: { start: Date }) => {
    const defaultDepartureDate = start.toISOString();
    navigate(`/driverSchedules/create?departureDate=${defaultDepartureDate}`);
  };

  const handleSelectEvent = (event: ScheduleEvent) => {
    navigate(`/driverSchedules/${event.id}/show`);
  };

  const handleDriverChange = (event: any) => {
    setSelectedDriver(event ? event : null);
  };

  const handleOriginCityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOriginCity(event.target.value);
  };

  const handleDestinationCityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDestinationCity(event.target.value);
  };

  return (
    <div style={{ height: "100vh" }}>
      {!driverId && (
        <SimpleForm
          toolbar={false}
          resource="driverSchedules"
          sx={{ marginBottom: 0, padding: 0 }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <ReferenceInput
              source="driver"
              reference="fleetAssignments"
              sx={{ flex: 2 }}
              alwaysOn
            >
              <AutocompleteInput
                optionText={(option) =>
                  `${option.truck.licensePlate ?? option.truck.plate} - ${
                    option.driver === null ? "未知司机" : option.driver.name
                  } - ${option.source ?? `线下收集`}`
                }
                label="选择司机与货车（可输入车牌号快速查找）"
                filterToQuery={createTrucksFilterQuery}
                noOptionsText="无选项"
                onChange={handleDriverChange}
                fullWidth
                sx={{ maxWidth: "400px" }}
              />
            </ReferenceInput>
            <TextField
              label="始发站城市名"
              variant="outlined"
              value={originCity || ""}
              onChange={handleOriginCityChange}
              sx={{ width: 320, marginBottom: 0, padding: 0 }}
            />
            <TextField
              label="终点站城市名"
              variant="outlined"
              value={destinationCity || ""}
              onChange={handleDestinationCityChange}
              sx={{ width: 320, marginBottom: 0, padding: 0 }}
            />
          </Box>
        </SimpleForm>
      )}

      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        style={{ height: 800 }}
        components={{
          event: EventComponent,
        }}
        messages={{
          date: "日期",
          time: "时间",
          event: "事件",
          allDay: "全天",
          week: "周",
          work_week: "工作周",
          day: "日",
          month: "月",
          previous: "上一个",
          next: "下一个",
          yesterday: "昨天",
          tomorrow: "明天",
          today: "今天",
          agenda: "调度事件",
          noEventsInRange: "此范围内没有事件。",
          showMore: (total) => `+${total} 更多`,
        }}
      />
    </div>
  );
};

export default ScheduleCalendar;